import { useCallback, useEffect, useState } from "react";

import useSegmentTrackEvent from "../../utils/useSegmentTrackEvent";
import { type TimeRange } from "./Dashboard/ClusterDetail/Components/TimeRangeSelector";

const baseKubernetesLensEventProperties = {
  pageType: "Dashboards",
  pageSubtype: "Kubernetes Lens",
  feature: "Kubernetes Lens",
};

const baseFsk8sEventProperties = {
  pageType: "Optimize",
  pageSubType: "PerfectScale",
  feature: "PerfectScale",
};

interface ClusterDetailViewedEvent {
  clusterId: string;
  clusterType: string;
}

interface ClusterAgentInstallStartedEvent {
  clusterId: string;
  clusterType: string;
  agentStatus: string;
}

interface K8sDashboardViewedEvent {
  timeRange: TimeRange;
  totalSpend: number;
  podWaste: number;
  totalClusters: number;
}

interface PerfectScaleLinkClickedEvent {
  customerHasPerfectScale: boolean;
  perfectScaleTenantId?: string;
}

type K8sTrackMetricsViewedEvent = {
  datasource?: "Kubernetes";
  metricName: string | null | undefined;
};

type KubernetesLensEventTypes = {
  "Cluster Detail Viewed": ClusterDetailViewedEvent;
  "K8s Dashboard Viewed": K8sDashboardViewedEvent;
  "Cluster Agent Install Started": ClusterAgentInstallStartedEvent;
  "Upsell Page Viewed": object;
  "Upgrade Options Button Clicked": object;
  "Upsell Notification Clicked": object;
  "K8s Metric Event Viewed": K8sTrackMetricsViewedEvent;
};

type Fsk8sEventTypes = {
  "PerfectScale Link Clicked": PerfectScaleLinkClickedEvent;
  "Upsell Page Viewed": object;
};

export function useTrackKubernetesLensEvent() {
  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });

  return useCallback(
    <T extends keyof KubernetesLensEventTypes>(type: T, options: KubernetesLensEventTypes[T]) => {
      trackEvent(type, {
        ...baseKubernetesLensEventProperties,
        ...options,
      });
    },
    [trackEvent]
  );
}

export function useTrackFsk8sEvent() {
  const { trackEvent } = useSegmentTrackEvent({ allowNullUser: false });

  return useCallback(
    <T extends keyof Fsk8sEventTypes>(type: T, options: Fsk8sEventTypes[T]) => {
      trackEvent(type, {
        ...baseFsk8sEventProperties,
        ...options,
      });
    },
    [trackEvent]
  );
}

interface UseTrackKubernetesLensEventOnceProps<T extends keyof KubernetesLensEventTypes> {
  name: T;
  properties: KubernetesLensEventTypes[T];
  condition?: boolean;
}

/* Wrapper over trackEvent, used for "page viewed" & "metrics" events. Avoids sending the event more than once */
export const useTrackKubernetesLensEventOnce = <T extends keyof KubernetesLensEventTypes>({
  name,
  properties,
  condition,
}: UseTrackKubernetesLensEventOnceProps<T>) => {
  const trackEvent = useTrackKubernetesLensEvent();

  const [eventWasSent, setEventWasSent] = useState(false);

  const sendEvent = useCallback(() => {
    trackEvent(name, properties);
    setEventWasSent(true);
  }, [name, properties, trackEvent]);

  useEffect(() => {
    if (condition && !eventWasSent) {
      sendEvent();
    }
  }, [sendEvent, condition, eventWasSent]);
};

interface UseTrackFsk8sEventOnceProps<T extends keyof Fsk8sEventTypes> {
  name: T;
  properties: Fsk8sEventTypes[T];
  condition?: boolean;
}

/* Wrapper over trackEvent, used for "page viewed" events. Avoids sending the event more than once */
export const useTrackFsk8sEventOnce = <T extends keyof Fsk8sEventTypes>({
  name,
  properties,
  condition,
}: UseTrackFsk8sEventOnceProps<T>) => {
  const trackEvent = useTrackFsk8sEvent();

  const [eventWasSent, setEventWasSent] = useState(false);

  const sendEvent = useCallback(() => {
    trackEvent(name, properties);
    setEventWasSent(true);
  }, [name, properties, trackEvent]);

  useEffect(() => {
    if (condition && !eventWasSent) {
      sendEvent();
    }
  }, [sendEvent, condition, eventWasSent]);
};
